/* eslint-disable react/jsx-props-no-spreading */
import type { GetStaticPropsContext, InferGetStaticPropsType } from 'next';
// eslint-disable-next-line no-use-before-define
import React from 'react';
import { useRouter } from 'next/router';

import { logger } from '@shared/utils';
import { DataLayerProvider } from '@shared/v2/contexts/DataLayerContext';

import { Meta } from '@/v2/components/global/Meta';
import { DestinationView } from '@/v2/components/pages/DestinationView';
import useEnrichProperties from '@/v2/components/pages/DestinationView/hooks/useEnrichProperties';
import useEnrichThemeDestination from '@/v2/components/pages/DestinationView/hooks/useEnrichThemeDestination';
import { LoadingView } from '@/v2/components/pages/LoadingView';
import { CampaignProvider } from '@/v2/contexts/campaign';
import { DestinationProvider } from '@/v2/contexts/destination';
import { getDestinationPage } from '@/v2/services/bff/getDestinationPage';
// using legacy context - idea is to add properties to destination context
import { PropertiesContentProvider } from '../../components/DestinationPage/hooks/usePropertiesContent';

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps = async ({
  params,
  preview,
}: GetStaticPropsContext<{ destinationName: string }>) => {
  try {
    const props = await getDestinationPage({
      destinationName: params?.destinationName,
      preview,
    });

    return {
      props,
      revalidate: 300,
    };
  } catch (error) {
    logger.error(error);
    return {
      props: {
        campaign: null,
        destination: null,
        importantInformation: null,
        meta: null,
        navigation: null,
        propertiesContent: null,
        campaigns: null,
        themeDestinations: null,
        informationMessage: null,
        warningMessage: null,
        marketingMessage: null,
      },
      notFound: true,
      revalidate: 300,
    };
  }
};

const dataLayerData = { pageName: 'Holiday theme' };

const DestinationPage = ({
  campaign,
  destination,
  meta,
  propertiesContent,
  importantInformation,
  navigation,
  campaigns,
  informationMessage,
  warningMessage,
  marketingMessage,
  themeDestinations,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const enrichedProperties = useEnrichProperties({
    destinationOrTheme: destination,
    propertiesContent,
    themeDestinations,
  });

  const enrichedDestination = useEnrichThemeDestination({
    destinationOrTheme: destination,
    properties: enrichedProperties,
  });

  const router = useRouter();

  // If the page is not yet generated, this will be displayed
  // initially until getStaticProps() finishes running
  if (router.isFallback) {
    return <LoadingView />;
  }

  return (
    destination &&
    enrichedDestination && (
      <DestinationProvider value={enrichedDestination}>
        <PropertiesContentProvider value={enrichedProperties}>
          <CampaignProvider value={campaign}>
            <DataLayerProvider value={dataLayerData}>
              <Meta {...meta} />
              <DestinationView
                key={destination.name}
                importantInformation={importantInformation}
                navigation={navigation}
                campaigns={campaigns}
                informationMessage={informationMessage}
                warningMessage={warningMessage}
                marketingMessage={marketingMessage}
              />
            </DataLayerProvider>
          </CampaignProvider>
        </PropertiesContentProvider>
      </DestinationProvider>
    )
  );
};

export default DestinationPage;
